import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { UI, changeText, dateShort, moneyShort, portfolioNarrative, value } from './operationalUi';
import type { OperationalResult } from './types';

export default function VerdictBand({ result, campaign = false }: { result: OperationalResult; campaign?: boolean }) {
  const row = result.selected,
    hero = result.story.hero,
    previous = !campaign && result.filters.historyView === 'previous',
    previousMetric = result.previousRuns?.summary.metrics.margin_per_day,
    previousCount = previousMetric?.pairCount || 0;
  const sentences = campaign ? result.verdict.campaign.slice(0, 2) : portfolioNarrative(result);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: campaign ? '220px minmax(0,1fr) 200px' : '240px minmax(0,1fr) 240px',
        gap: 2,
        py: 1,
        flexShrink: 0,
        minHeight: 100,
        minWidth: 0,
        '@container (max-width: 1050px)': {
          gridTemplateColumns: '250px minmax(0,1fr)',
          '& .flash-verdict-counts': { gridColumn: '1 / -1', display: 'flex', gap: 3, alignItems: 'center' },
        },
        '@container (max-width:600px)': {
          gridTemplateColumns: 'minmax(0,1fr)',
          '& .flash-verdict-counts': { display: 'block', minWidth: 0 },
        },
      }}
    >
      <Box sx={{ borderLeft: `3px solid ${UI.mint}`, pl: 2, alignSelf: 'start' }}>
        <Typography
          sx={{
            fontSize: campaign ? 38 : 44,
            fontWeight: 600,
            color: UI.teal,
            lineHeight: 1.12,
            letterSpacing: -1,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {campaign
            ? value(row?.campaign.status === 'completed' ? row.actual.margin_per_day : row?.gap, 'currency')
            : previous
              ? value(previousMetric?.difference, 'currency')
              : moneyShort(hero.amount)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.75, lineHeight: 1.4 }}>
          {campaign
            ? row?.campaign.status === 'completed'
              ? `Adjusted margin/day · peer median ${value(row.peerMedian.margin_per_day, 'currency')}`
              : `v peer median pace at today’s rate, next ${Math.min(row?.daysLeft || 0, 28)} days`
            : previous
              ? 'Margin/day v previous Flash'
              : hero.direction === 'unavailable'
                ? 'Weekly comparison unavailable'
                : `a week ${hero.direction === 'down' ? 'less' : 'more'} margin${result.weekly.exclusionIds.length ? ' with exclusions' : ''}`}
        </Typography>
        {previous && (
          <Typography sx={{ fontSize: 12, color: UI.muted }}>
            {previousCount} paired campaigns · {changeText(previousMetric?.changePct)}
          </Typography>
        )}
        {!campaign && !previous && hero.direction !== 'unavailable' && (
          <Typography sx={{ fontSize: 12, color: UI.muted }}>
            {dateShort(hero.recentStart)}–{dateShort(hero.recentEnd)} v {dateShort(hero.priorStart)}–
            {dateShort(hero.priorEnd)}
          </Typography>
        )}
      </Box>
      <Box sx={{ alignSelf: 'center' }}>
        {(sentences.length ? sentences : ['No campaigns have a peer group under these filters.']).map((text, i) => (
          <Typography
            key={i}
            sx={{
              fontSize: 14,
              lineHeight: 1.35,
              mb: i === sentences.length - 1 ? 0 : 0.6,
              color: UI.ink,
            }}
          >
            {text}
          </Typography>
        ))}
      </Box>
      {campaign ? (
        <Box sx={{ alignSelf: 'center' }}>
          <Typography sx={{ fontSize: 13, color: UI.muted }}>
            Peer group: {row?.country}, {row?.level === 'country' ? 'country peers' : row?.destination}, {row?.type}
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 0.75 }}>
            {row?.coverage.sales || 0} sales-observed days of {row?.daysObserved || 0} · {row?.n || 0} peers
          </Typography>
        </Box>
      ) : (
        <Box className="flash-verdict-counts" sx={{ alignSelf: 'center' }}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            {[
              { n: result.counts.below, label: 'well below peers', color: UI.below.color },
              { n: result.counts.inLine, label: 'in line with peers', color: UI.teal },
              { n: result.counts.above, label: 'well above peers', color: UI.above.color },
            ].map((item) => (
              <Box key={item.label}>
                <Typography sx={{ fontSize: 25, lineHeight: 1.1, color: item.color }}>{item.n}</Typography>
                <Typography sx={{ fontSize: 12, color: UI.muted, maxWidth: 80, mt: 0.5 }}>{item.label}</Typography>
              </Box>
            ))}
          </Stack>
          <Typography sx={{ fontSize: 12, color: UI.muted, mt: 1 }}>
            {result.coverage.compared}/{result.coverage.total} compared · {result.counts.tooEarly} too early
          </Typography>
        </Box>
      )}
    </Box>
  );
}
